import { isEqual } from "es-toolkit";
import { atom } from "jotai";
import { atomFamily } from "jotai/utils";
import * as oauth from "oauth4webapi";

import { env } from "../env/env";
import type { DocumentToken, PasswordExchangeResponse } from "./document-auth.types";

/** Time before expiry to trigger a refresh (60 seconds) */
const REFRESH_THRESHOLD_MS = 60 * 1000;

// Cache the Authorization Server metadata to avoid repeated discovery requests
let asMetadataPromise: Promise<oauth.AuthorizationServer> | null = null;

/**
 * Fetches and caches the Auth0 Authorization Server metadata.
 * Uses OIDC discovery (.well-known/openid-configuration).
 * Clears cache on error to allow retry on subsequent calls.
 */
const getAuthServerMetadata = async (): Promise<oauth.AuthorizationServer> => {
	if (!asMetadataPromise) {
		const issuer = new URL(`https://${env.authSettings.auth0Domain}/`);
		asMetadataPromise = oauth
			.discoveryRequest(issuer, { algorithm: "oidc" })
			.then((response) => oauth.processDiscoveryResponse(issuer, response))
			.catch((error) => {
				asMetadataPromise = null; // Clear cache on error to allow retry
				throw error;
			});
	}
	return asMetadataPromise;
};

/**
 * Refreshes a document token using the refresh_token grant.
 * Uses the public client (no client secret required).
 *
 * @throws Error if the refresh fails (including OAuth2 protocol errors)
 */
export const refreshDocumentToken = async (
	refreshToken: string
): Promise<{ access_token: string; refresh_token?: string; expires_in: number }> => {
	const as = await getAuthServerMetadata();
	const client: oauth.Client = { client_id: env.authSettings.publishAuth0ClientId };

	const response = await oauth.refreshTokenGrantRequest(
		as,
		client,
		oauth.None(), // Public client - no client secret
		refreshToken
	);

	// processRefreshTokenResponse throws on OAuth2 errors (ResponseBodyError)
	// and WWW-Authenticate challenges (WWWAuthenticateChallengeError)
	const result = await oauth.processRefreshTokenResponse(as, client, response);

	return {
		access_token: result.access_token,
		refresh_token: result.refresh_token,
		expires_in: result.expires_in ?? 3600,
	};
};

// Internal atom storing all document tokens by key
const $documentTokensMap = atom<Record<string, DocumentToken>>({});
$documentTokensMap.debugLabel = "documentTokensMap";

/**
 * Creates a unique key for a document token.
 */
const makeTokenKey = (workspaceId: string, documentId: string): string =>
	`${workspaceId}:${documentId}`;

/**
 * Atom family for accessing/setting a specific document's token.
 * Tokens are stored in-memory only and cleared on page refresh.
 */
export const $documentToken = atomFamily(
	(params: { workspaceId: string; documentId: string }) =>
		atom(
			(get) => {
				const key = makeTokenKey(params.workspaceId, params.documentId);
				return get($documentTokensMap)[key] ?? null;
			},
			(get, set, token: DocumentToken | null) => {
				const key = makeTokenKey(params.workspaceId, params.documentId);
				const current = get($documentTokensMap);
				if (token === null) {
					const { [key]: _, ...rest } = current;
					set($documentTokensMap, rest);
				} else {
					set($documentTokensMap, { ...current, [key]: token });
				}
			}
		),
	isEqual
);

/**
 * Converts a password exchange response to a DocumentToken.
 */
export const createDocumentToken = (
	response: PasswordExchangeResponse,
	workspaceId: string,
	documentId: string
): DocumentToken => ({
	accessToken: response.access_token,
	refreshToken: response.refresh_token,
	expiresAt: Date.now() + response.expires_in * 1000,
	documentId,
	workspaceId,
});

/**
 * Result of attempting to get a valid token.
 */
export type GetValidTokenResult =
	| { success: true; accessToken: string }
	| { success: false; reason: "no_token" | "refresh_failed" };

/**
 * Gets a valid access token for a document, refreshing if necessary.
 * Returns a result object indicating success/failure.
 *
 * This is a standalone function rather than an atom because it needs
 * to perform async refresh operations and update state.
 */
export const getValidDocumentToken = async (
	token: DocumentToken | null,
	setToken: (token: DocumentToken | null) => void
): Promise<GetValidTokenResult> => {
	if (!token) {
		return { success: false, reason: "no_token" };
	}

	const now = Date.now();
	const timeUntilExpiry = token.expiresAt - now;

	// Token is still valid and not near expiry
	if (timeUntilExpiry > REFRESH_THRESHOLD_MS) {
		return { success: true, accessToken: token.accessToken };
	}

	// Token is expired or near expiry - try to refresh
	if (!token.refreshToken) {
		// No refresh token available, can't refresh
		// Return the current token even if expired - the API call will fail
		// and trigger re-authentication
		return { success: true, accessToken: token.accessToken };
	}

	try {
		const refreshed = await refreshDocumentToken(token.refreshToken);
		const newToken: DocumentToken = {
			...token,
			accessToken: refreshed.access_token,
			// Use new refresh token if provided (token rotation), otherwise keep existing
			refreshToken: refreshed.refresh_token ?? token.refreshToken,
			expiresAt: now + refreshed.expires_in * 1000,
		};
		setToken(newToken);
		return { success: true, accessToken: newToken.accessToken };
	} catch (error) {
		console.error("Failed to refresh document token:", error);
		// Clear the invalid token
		setToken(null);
		return { success: false, reason: "refresh_failed" };
	}
};
